import { useEffect, useState } from "react";
import { MeterCutoffsDataType, UseMeterCutOffsProps } from "../types";
import { DEFAULT_PAGE_LIMIT } from "../constants/api";
import {
  fetchDailyCutoffsAsync,
  fetchMonthlyCutoffsAsync,
} from "../actions/meters.actions";
import { useMeterStore } from "../store/meter.store";

export default function useMeterCutoffs({ meter }: UseMeterCutOffsProps) {
  const tabValue = useMeterStore((state) => state.tabSelector);
  const setTabValue = useMeterStore((state) => state.setTabSelector);
  const setDailyCutoffRecordId = useMeterStore(state => state.setDailyCutoffRecordId)
  const dailyCutoffRecordId = useMeterStore(state => state.dailyCutoffRecordId)
  const { comeFromMeterSidebarSelector, setMeterSidebarSelector } =
    useMeterStore((state) => state);

  const [data, setData] = useState<MeterCutoffsDataType>({
    dailyCutoffs: {
      loading: false,
      totalPages: 1,
      page: 1,
      limit: DEFAULT_PAGE_LIMIT,
      error: false,
      results: [],
    },
    monthlyCutoffs: {
      loading: false,
      totalPages: 1,
      page: 1,
      limit: DEFAULT_PAGE_LIMIT,
      error: false,
      results: [],
    },
  });

  const fetchInitialData = () => {
    setData((prevState) => ({
      dailyCutoffs: { ...prevState.dailyCutoffs, loading: true },
      monthlyCutoffs: { ...prevState.monthlyCutoffs, loading: true },
    }));

    let meterNum = meter?.meterSerialNo;
    let page = 1;
    let limit = DEFAULT_PAGE_LIMIT;

    const requests = [
      fetchDailyCutoffsAsync(meterNum, page, limit + 1),
      fetchMonthlyCutoffsAsync(meterNum, page, limit + 1),
    ];

    Promise.allSettled(requests).then((results) => {
      results?.forEach((result, index) => {
        if (index === 0) {
          let dailyCutoffsResObj: MeterCutoffsDataType["dailyCutoffs"];

          if (result?.status === "fulfilled") {
            let res = (result?.value as any)?.data as any;
            const rawData = res?.data || [];
            const displayData = rawData.slice(0, limit);

            dailyCutoffsResObj = {
              loading: false,
              totalPages: res?.totalItems ? Math.ceil(res.totalItems / limit) : res?.totalPages,
              page: res?.currentPage,
              limit: limit,
              error: false,
              results: displayData.map((item: any, i: number) => ({
                id: item?.id,
                deviceDateTime: item?.deviceTime,
                totalVolume: item?.totalVolume,
                previousVolume: rawData[i + 1]?.totalVolume ?? null,
                temperature: item?.temperature,
                errorCode: Number(item?.errorCode) || 0,
                isAlarmTelegram: item?.isAlarmTelegram || false,
                alarmSummary: item?.alarmSummary || null,
              })),
            };
          } else {
            dailyCutoffsResObj = {
              loading: false,
              totalPages: 1,
              page: 1,
              limit: DEFAULT_PAGE_LIMIT,
              error: true,
              results: [],
            };
          }

          setData((prevState) => ({
            ...prevState,
            dailyCutoffs: dailyCutoffsResObj,
          }));
        } else if (index === 1) {
          let monthlyCutoffsResObj: MeterCutoffsDataType["monthlyCutoffs"];

          if (result?.status === "fulfilled") {
            let res = (result?.value as any)?.data as any;
            const rawMonthly = res?.data || [];
            const displayMonthly = rawMonthly.slice(0, limit);

            monthlyCutoffsResObj = {
              loading: false,
              totalPages: res?.totalItems ? Math.ceil(res.totalItems / limit) : res?.totalPages || 1,
              page: res?.currentPage,
              limit: limit,
              error: false,
              results: displayMonthly.map((item: any, i: number) => ({
                id: item?.id,
                month: item?.month,
                cutoffDateTime: item?.["CutOff Date-Time"],
                consumption: item?.["MonthlyConsumption"],
                reading: item?.["Consumtion @ CutOff"],
                previousReading: rawMonthly[i + 1]?.["Consumtion @ CutOff"] ?? null,
              })),
            };
          } else {
            monthlyCutoffsResObj = {
              loading: false,
              totalPages: 1,
              page: 1,
              limit: DEFAULT_PAGE_LIMIT,
              error: true,
              results: [],
            };
          }

          setData((prevState) => ({
            ...prevState,
            monthlyCutoffs: monthlyCutoffsResObj,
          }));
        }
      });
    });
  };

  const fetchDailyCutoffs = (meterNum: string, page: number, limit: number) => {
    setData((prevState) => ({
      ...prevState,
      dailyCutoffs: { ...prevState.dailyCutoffs, loading: true },
    }));

    fetchDailyCutoffsAsync(meterNum, page, limit + 1)
      .then((response) => {
        let res = (response as any)?.data;
        const rawData = res?.data || [];
        const displayData = rawData.slice(0, limit);

        let dailyCutoffsResObj = {
          loading: false,
          totalPages: res?.totalItems ? Math.ceil(res.totalItems / limit) : res?.totalPages,
          page: res?.currentPage,
          limit: limit,
          error: false,
          results: displayData.map((item: any, i: number) => ({
            id: item?.id,
            deviceDateTime: item?.deviceTime,
            totalVolume: item?.totalVolume,
            previousVolume: rawData[i + 1]?.totalVolume ?? null,
            temperature: item?.temperature,
            errorCode: item?.errorCode,
            isAlarmTelegram: item?.isAlarmTelegram || false,
            alarmSummary: item?.alarmSummary || null,
          })),
        };

        setData((prevState) => ({
          ...prevState,
          dailyCutoffs: dailyCutoffsResObj,
        }));
      })
      .catch(() => {
        let dailyCutoffsResObj = {
          loading: false,
          totalPages: 1,
          page: 1,
          limit: DEFAULT_PAGE_LIMIT,
          error: true,
          results: [],
        };

        setData((prevState) => ({
          ...prevState,
          dailyCutoffs: dailyCutoffsResObj,
        }));
      });
  };

  const onDailyCutoffRowClick = (id: number) => {
    setDailyCutoffRecordId(String(id))
  }

  const fetchMonthlyCutoffs = (meterNum: string, page: number, limit: number) => {
    setData((prevState) => ({
      ...prevState,
      monthlyCutoffs: { ...prevState.monthlyCutoffs, loading: true },
    }));

    fetchMonthlyCutoffsAsync(meterNum, page, limit + 1)
      .then((response) => {
        let res = (response as any)?.data;
        const rawMonthly = res?.data || [];
        const displayMonthly = rawMonthly.slice(0, limit);

        let monthlyCutoffsResObj = {
          loading: false,
          totalPages: res?.totalItems ? Math.ceil(res.totalItems / limit) : res?.totalPages || 1,
          page: res?.currentPage,
          limit: limit,
          error: false,
          results: displayMonthly.map((item: any, i: number) => ({
            id: item?.id,
            month: item?.month,
            cutoffDateTime: item?.["CutOff Date-Time"],
            consumption: item?.["MonthlyConsumption"],
            reading: item?.["Consumtion @ CutOff"],
            previousReading: rawMonthly[i + 1]?.["Consumtion @ CutOff"] ?? null,
          })),
        };

        setData((prevState) => ({
          ...prevState,
          monthlyCutoffs: monthlyCutoffsResObj,
        }));
      })
      .catch(() => {
        let monthlyCutoffsResObj = {
          loading: false,
          totalPages: 1,
          page: 1,
          limit: DEFAULT_PAGE_LIMIT,
          error: true,
          results: [],
        };

        setData((prevState) => ({
          ...prevState,
          monthlyCutoffs: monthlyCutoffsResObj,
        }));
      });
  };

  const handleDailyCutoffsPageChange = (page: number | null) => {
    let meterNum = meter?.meterSerialNo;
    let limit = data.dailyCutoffs.limit;

    fetchDailyCutoffs(meterNum, page || 1, limit);
  };

  const handleDailyCutoffsPageLimitSelect = (limit: string | null) => {
    let meterNum = meter?.meterSerialNo;
    let page = 1;

    fetchDailyCutoffs(
      meterNum,
      page,
      limit ? parseInt(limit) : DEFAULT_PAGE_LIMIT
    );
  };

  const handleMonthlyCutoffsPageChange = (page: number | null) => {
    let meterNum = meter?.meterSerialNo;
    let limit = data.dailyCutoffs.limit;

    fetchMonthlyCutoffs(meterNum, page || 1, limit);
  };

  const handleMonthlyCutoffsPageLimitSelect = (limit: string | null) => {
    let meterNum = meter?.meterSerialNo;
    let page = 1;

    fetchMonthlyCutoffs(
      meterNum,
      page,
      limit ? parseInt(limit) : DEFAULT_PAGE_LIMIT
    );
  };

  const goBack = () => {
    setMeterSidebarSelector(comeFromMeterSidebarSelector);
  };

  useEffect(() => {
    setTabValue("daily");

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    tabValue,
    setTabValue,
    handleDailyCutoffsPageChange,
    handleDailyCutoffsPageLimitSelect,
    handleMonthlyCutoffsPageChange,
    handleMonthlyCutoffsPageLimitSelect,
    goBack,
    onDailyCutoffRowClick,
    dailyCutoffRecordId
  };
}
